import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RiSettings4Line, RiTimeLine, RiVipCrownLine, RiCalendarEventLine, RiSaveLine, RiBuildingLine } from 'react-icons/ri';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const SystemSettings = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [settings, setSettings] = useState({
        name: '',
        timezone: '',
        subscriptionStatus: '',
        trialEndsAt: null,
        logoUrl: '',
        contactEmail: '',
        contactPhone: '',
        address: '',
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await api.get('/organization/settings');
            if (response.data.success) {
                const org = response.data.organization;
                setSettings({
                    ...org,
                    logoUrl: org.logoUrl || '',
                    contactEmail: org.contactEmail || '',
                    contactPhone: org.contactPhone || '',
                    address: org.address || ''
                });
            }
        } catch (error) {
            console.error('Fetch system settings error:', error);
            toast.error(t('failed_to_load_system_data'));
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (e) => {
        try {
            if (!e.target.files || e.target.files.length === 0) {
                return;
            }
            const file = e.target.files[0];
            setUploadingLogo(true);
            
            const formData = new FormData();
            formData.append('logo', file);
            
            const response = await api.post('/organization/logo', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
                
            if (response.data.success) {
                setSettings({ ...settings, logoUrl: response.data.logoUrl });
                toast.success(t('logo_upload_success') || 'Logo uploaded successfully');
            } else {
                throw new Error(response.data.message || 'Error from server');
            }
        } catch (error) {
            console.error('Logo upload error:', error);
            toast.error(error.response?.data?.message || error.message || t('error_uploading_logo'));
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const response = await api.put('/organization/settings', {
                name: settings.name,
                timezone: settings.timezone,
                logoUrl: settings.logoUrl,
                contactEmail: settings.contactEmail,
                contactPhone: settings.contactPhone,
                address: settings.address,
            });
            if (response.data.success) {
                toast.success(t('update_system_success'));
            }
        } catch (error) {
            console.error('Update system settings error:', error);
            toast.error(error.response?.data?.message || t('error_updating_system_settings'));
        } finally {
            setLoading(false);
        }
    };

    const timezones = [
        'UTC',
        'Asia/Baghdad',
        'Asia/Erbil',
        'Asia/Dubai',
        'Europe/London',
        'America/New_York',
    ];

    return (
        <div className="space-y-6">
            {/* Organization Details */}
            <section className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                    <div className="p-2 rounded-lg bg-red-50 text-red-600">
                        <RiBuildingLine size={20} />
                    </div>
                    <h2 className="text-lg font-bold text-zinc-900">{t('organization_settings')}</h2>
                </div>

                <form onSubmit={handleUpdate} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('organization_name')}
                        </label>
                        <input
                            type="text"
                            value={settings.name}
                            onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                            required
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('timezone')}
                        </label>
                        <div className="relative">
                            <RiTimeLine className="absolute start-3.5 top-1/2 -translate-y-1/2 text-zinc-400 w-5 h-5" />
                            <select
                                value={settings.timezone}
                                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                                className="w-full ps-11 pe-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all appearance-none bg-white"
                            >
                                {timezones.map((tz) => (
                                    <option key={tz} value={tz}>
                                        {tz}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="md:col-span-1 border-t border-zinc-100 pt-6 mt-2">
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('contact_email') || 'Contact Email'}
                        </label>
                        <input
                            type="email"
                            value={settings.contactEmail}
                            onChange={(e) => setSettings({ ...settings, contactEmail: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                            placeholder="admin@example.com"
                        />
                    </div>

                    <div className="md:col-span-1 border-t border-zinc-100 pt-6 mt-2">
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('contact_phone') || 'Contact Phone'}
                        </label>
                        <input
                            type="text"
                            value={settings.contactPhone}
                            onChange={(e) => setSettings({ ...settings, contactPhone: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                            placeholder="+1 234 567 8900"
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('office_address') || 'Office Address'}
                        </label>
                        <textarea
                            value={settings.address}
                            onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                            rows="2"
                            placeholder="123 Main St, City, Country"
                        ></textarea>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-zinc-700 mb-2">
                            {t('organization_logo') || 'Organization Logo'}
                        </label>
                        <div className="flex items-center gap-4">
                            {settings.logoUrl && (
                                <div className="p-2 bg-zinc-50 rounded-xl border border-zinc-200 flex items-center justify-center shrink-0">
                                    <img src={settings.logoUrl} alt="Organization Logo" className="h-16 w-auto object-contain" />
                                </div>
                            )}
                            <div className="flex-1">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoUpload}
                                    disabled={uploadingLogo}
                                    className="w-full text-sm text-zinc-500 file:me-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-600 hover:file:bg-red-100 transition-all cursor-pointer disabled:opacity-50"
                                />
                                {uploadingLogo && <p className="text-xs font-medium text-zinc-500 mt-2">{t('uploading') || 'Uploading...'}</p>}
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-2 pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all active:scale-95 disabled:opacity-50 font-bold shadow-lg shadow-red-100"
                        >
                            <RiSaveLine className="w-5 h-5" />
                            {loading ? t('saving') : t('save_changes')}
                        </button>
                    </div>
                </form>
            </section>

            {/* Subscription Section — hidden for custom1 (SUBSCRIPTION_BYPASS) */}

        </div>
    );
};

export default SystemSettings;
